/**
 * Avatar style utilities. Root (wrapper), fallback (initials), image.
 */
import { cn } from "@/utils/cn";
import type { AvatarSize, AvatarVariant, AvatarBadgeVariant } from "./Avatar.types";

/** Size → dimensions (w, h). */
const sizeDimensionClasses: Record<AvatarSize, string> = {
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-12 h-12",
  xl: "w-16 h-16",
  "2xl": "w-20 h-20",
};

/** Size → fallback text size. */
const sizeTextClasses: Record<AvatarSize, string> = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-xl",
  xl: "text-2xl",
  "2xl": "text-3xl",
};

/** Variant → fallback background + text. */
const variantClasses: Record<AvatarVariant, string> = {
  gradient: "bg-gradient-to-br from-gradient-purple to-gradient-pink text-white",
  neutral: "bg-gray-100 text-dark-invirtus",
};

/** Root: flex, dimensions. Overflow-visible so AvatarBadge is not clipped. */
export function getAvatarRootClasses(
  size: AvatarSize,
  className?: string
): string {
  return cn(
    "relative flex shrink-0 overflow-visible rounded-full select-none",
    sizeDimensionClasses[size],
    className
  );
}

/** Inner wrapper: clips image/fallback to circle. */
export function getAvatarInnerClasses(): string {
  return "absolute inset-0 overflow-hidden rounded-full";
}

/** Fallback: initials when no image or image fails. */
export function getAvatarFallbackClasses(
  size: AvatarSize,
  variant: AvatarVariant,
  className?: string
): string {
  return cn(
    "flex size-full items-center justify-center font-semibold",
    sizeTextClasses[size],
    variantClasses[variant],
    className
  );
}

/** Image: aspect-square, object-cover. */
export function getAvatarImageClasses(className?: string): string {
  return cn("aspect-square size-full object-cover", className);
}

/** AvatarBadge variant → background + text (design-system tokens). */
const avatarBadgeVariantClasses: Record<AvatarBadgeVariant, string> = {
  brand: "bg-[var(--color-purple)] text-dark-invirtus",
  success: "bg-[var(--color-green)] text-dark-invirtus",
  warning: "bg-[var(--color-yellow)] text-dark-invirtus",
  danger: "bg-[var(--color-brand-red)] text-white",
  info: "bg-[var(--color-blue)] text-dark-invirtus",
  dark: "bg-[var(--color-dark-invirtus)] text-white",
  neutral: "bg-[var(--color-grey)] text-white",
};

/** Badge: bottom-right corner, snug to avatar edge. Sizes via group-data-[size]/avatar. */
export function getAvatarBadgeClasses(
  variant: AvatarBadgeVariant = "brand",
  className?: string
): string {
  return cn(
    "absolute -right-px -bottom-px z-10 inline-flex items-center justify-center rounded-full ring-2 ring-background select-none",
    avatarBadgeVariantClasses[variant],
    "group-data-[size=sm]/avatar:size-2 group-data-[size=sm]/avatar:[&>svg]:hidden",
    "group-data-[size=md]/avatar:size-2.5 group-data-[size=md]/avatar:[&>svg]:size-2.5",
    "group-data-[size=lg]/avatar:size-3.5 group-data-[size=lg]/avatar:[&>svg]:size-3",
    "group-data-[size=xl]/avatar:size-4 group-data-[size=xl]/avatar:[&>svg]:size-3.5",
    "group-data-[size=2xl]/avatar:size-5 group-data-[size=2xl]/avatar:[&>svg]:size-4",
    className
  );
}

/** Group: overlapping avatars with ring. */
export function getAvatarGroupClasses(className?: string): string {
  return cn(
    "flex -space-x-2 [&>div]:ring-2 [&>div]:ring-background [&>div]:rounded-full",
    className
  );
}

/** Group count: +N pill, same size as avatars in group. */
export function getAvatarGroupCountClasses(
  size: AvatarSize,
  className?: string
): string {
  return cn(
    "relative flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground ring-2 ring-background text-sm font-medium",
    sizeDimensionClasses[size],
    "[&>svg]:size-4",
    size === "sm" && "[&>svg]:size-3 text-xs",
    (size === "lg" || size === "xl") && "[&>svg]:size-5",
    size === "2xl" && "[&>svg]:size-6 text-base",
    className
  );
}

/** Legacy: combined root+fallback for initials-only. */
export function getAvatarClasses(
  size: AvatarSize,
  variant: AvatarVariant,
  className?: string
): string {
  return cn(
    getAvatarRootClasses(size),
    getAvatarFallbackClasses(size, variant),
    className
  );
}
